import React, { useRef } from 'react';
import {
  QueryClientProvider,
  QueryClient,
  useQuery,
} from '@tanstack/react-query';

import { AshbyJob } from './ashby_types';
import categorize_and_sort_jobs from './categorize_and_sort_jobs';
import AshbyJobsList from './AshbyJobsList';
import AshbyJobsLoading from './AshbyJobsLoading';
import AshbyJobsError from './AshbyJobsError';

import 'stylesheets/about_hiring/ashby_jobs.scss';

// We pull jobs from the CareerPuck API, but they just act as a relay to the
// Ashby API while replacing the job URLs with the CareerPuck hosted pages. The
// format of the data (mostly) matches what we would get if pulled from Ashby
// directly.
const ASHBY_POSTINGS_URL =
  'https://api.careerpuck.com/v1/public/job-boards/academia/ashby';

async function getJobs(signal?: AbortSignal): Promise<AshbyJob[]> {
  const resp = await fetch(ASHBY_POSTINGS_URL, { signal });
  const jobData: { apiVersion: string; jobs: AshbyJob[] } = await resp.json();
  return jobData.jobs.filter((job: AshbyJob) => job.isListed);
}

type Props = {
  // If non-null, only render a single department worth of job descriptions
  showSingleDepartment?: string;
};

const AshbyJobs: React.FC<Props> = ({ showSingleDepartment }) => {
  const {
    isLoading,
    isError,
    data: jobs,
  } = useQuery({
    queryKey: ['ashbyjobs'],
    queryFn: ({ signal }) => getJobs(signal),
    retry: false,
  });

  if (isError) {
    return <AshbyJobsError />;
  }

  if (isLoading) {
    return <AshbyJobsLoading />;
  }

  let jobsByDepartment = categorize_and_sort_jobs(jobs);

  if (jobsByDepartment.length > 0 && showSingleDepartment) {
    const singleDepartmentsJobs = jobsByDepartment.find(
      ([department, _jobs]) => department === showSingleDepartment,
    );
    if (!singleDepartmentsJobs) {
      // If we can't find jobs for the specified department, just show the first department in the
      // list.
      jobsByDepartment = [['Open roles', jobsByDepartment[0][1]]];
    } else {
      jobsByDepartment = [['Open roles', singleDepartmentsJobs[1]]];
    }
  }

  return (
    <AshbyJobsList
      jobsByDepartment={jobsByDepartment}
      showAllRolesCta={!!showSingleDepartment}
    />
  );
};

const AshbyJobsWithQuery = (props: Props) => {
  const queryClient = useRef(new QueryClient());

  return (
    <QueryClientProvider client={queryClient.current}>
      <AshbyJobs {...props} />
    </QueryClientProvider>
  );
};

export default AshbyJobsWithQuery;
